import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import MultiSelectInput from "./MultiSelectInput";
import { useSelector } from "react-redux";
import { ApiCall } from "../helper/axios";
import { selectToken } from "../redux/authSlice";
// MultiSelectInput only cycles three chip classes (and two of them are near-identical blues), so a longer client
// list reads as two colours. These soft background / text pairs give every chip its own tone instead.
const CHIP_TONES = [
    { bg: "#F9F5FF", color: "#6941C6" }, // purple
    { bg: "#EFF8FF", color: "#175CD3" }, // blue
    { bg: "#ECFDF3", color: "#067647" }, // green
    { bg: "#FFF6ED", color: "#C4320A" }, // orange
    { bg: "#FDF2FA", color: "#C11574" }, // pink
    { bg: "#F0FDF9", color: "#107569" }, // teal
    { bg: "#EEF4FF", color: "#3538CD" }, // indigo
    { bg: "#FEF6EE", color: "#B93815" }, // amber
];
// One rule per tone, repeating every CHIP_TONES.length chips; the "+N" chip keeps its own grey.
const chipToneStyles = Object.fromEntries(CHIP_TONES.flatMap((t, i) => [
    [
        `& .multicolor-tag-select .MuiSelect-select .MuiChip-root:not(.multicolor-more):nth-of-type(${CHIP_TONES.length}n+${i + 1})`,
        { backgroundColor: t.bg, "& .MuiChip-label": { color: t.color } },
    ],
]));
/**
 * Edit User drawer: the active clients this person is assigned to, read from the Client List's own endpoint
 * (the same data the Client List's AM and Operator filters show). Two calls cover every role they can hold on a
 * client: `am_id` (lead / assigned AM) and `operator_id` (leads), merged by client id.
 */
export default function UserAssignedClients({ userId }) {
    const token = useSelector(selectToken);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        if (!userId)
            return;
        let cancelled = false;
        setLoading(true);
        setFailed(false);
        const fetchBy = async (filter) => {
            const params = new URLSearchParams({ [filter]: userId, status: "active", page: "1", limit: "500" });
            const res = await ApiCall("GET", `/api/client-store/list?${params.toString()}`, "", 
            // The client list follows the Clients module's client scope, not the Users page's.
            { Authorization: `Bearer ${token}`, "x-module-key": "clients" });
            const body = res?.data;
            if (!body?.success)
                throw new Error(body?.message || "Failed to load clients");
            return (body.data?.clients || []).map((c) => ({ _id: String(c._id), name: c.name || "" }));
        };
        (async () => {
            try {
                const [asAm, asOperator] = await Promise.all([fetchBy("am_id"), fetchBy("operator_id")]);
                if (cancelled)
                    return;
                const byId = new Map();
                [...asAm, ...asOperator].forEach((c) => byId.set(c._id, c));
                setClients([...byId.values()].sort((a, b) => a.name.localeCompare(b.name)));
            }
            catch {
                if (!cancelled) {
                    setClients([]);
                    setFailed(true);
                }
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [userId, token]);
    // Same field as Employee Portfolio (MultiSelectInput): chips in a box, "+N" to expand. Read-only, but NOT
    // disabled (that greys the whole field out): the wrapper below swallows the clicks / keys that would open the
    // menu, and readOnlyChips drops the remove "x"; the "+N" chip's own click still goes through.
    const options = clients.map((c) => ({
        value: c._id,
        label: c.name,
        chipColor: "#f9f5ff",
        textColor: "#6941c6",
    }));
    return (_jsx(Box, { sx: { mt: 1, ...chipToneStyles }, onMouseDownCapture: (e) => { e.preventDefault(); e.stopPropagation(); }, onKeyDownCapture: (e) => { if (e.key !== "Tab") {
            e.preventDefault();
            e.stopPropagation();
        } }, children: _jsx(MultiSelectInput, { name: "assigned_clients", label: loading || failed ? "Client List" : `Client List (${clients.length})`, value: clients.map((c) => c._id), options: options, onChange: () => { }, placeholder: loading ? "Loading clients..." : failed ? "Could not load the client list." : "No clients assigned.", maxDisplay: 30, readOnlyChips: true, hideSelectAll: true }) }));
}
